import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { useAuthStore } from '@/features/auth/authStore'
import { ERoleType } from '@/features/user-management/constants'
import { ROUTES } from '@/utils/routes'

/**
 * Guards child routes by enforcing authentication plus optional role/permission rules.
 */
interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRoles: ERoleType[]
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
}) => {
  const location = useLocation()

  // Authentication state is strictly managed by AuthProvider before Router renders
  // We can safely rely 100% on the synchronous Zustand store here.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const userRoles = useAuthStore.getState().getRoles()

  // If no token at all, redirect immediately
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />
  }

  const needsRoleCheck = requiredRoles.length > 0

  if (needsRoleCheck) {
    const roleAllowed = userRoles.some((role) => requiredRoles.includes(role))
    if (!roleAllowed) {
      return (
        <Navigate to={ROUTES.UNAUTHORIZED} state={{ from: location }} replace />
      )
    }
  }

  return <>{children}</>
}

export default ProtectedRoute
